import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { LIVE_STATUSES, MAX_GUESTS, type GuestInvite, type LiveStatus } from '@liveforge/shared';
import { Button } from '../components/ui/button';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { Field, FormError } from '../components/ui/field';
import { InlineLoader, LoadingScreen } from '../components/ui/spinner';
import { StatusBadge } from '../components/ui/status-badge';
import { useGuestInvites } from '../hooks/use-guests';
import { useLive } from '../hooks/use-lives';
import { errorMessage } from '../lib/api';
import { startDownload } from '../lib/download';
import {
  copyToClipboard,
  formatDateTime,
  fromDateTimeLocalValue,
  toDateTimeLocalValue,
} from '../lib/format';
import {
  createGuestInvite,
  deleteGuestInvite,
  recordingDownloadLink,
  updateLive,
} from '../lib/resources';

function inviteUrl(token: string): string {
  return `${window.location.origin}/guest/${token}`;
}

export function LiveDetailPage() {
  const { liveId } = useParams<{ liveId: string }>();
  const navigate = useNavigate();
  const { live, loading, error, setLive } = useLive(liveId);
  const {
    invites,
    loading: invitesLoading,
    error: invitesError,
    setInvites,
  } = useGuestInvites(liveId);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [status, setStatus] = useState<LiveStatus>('draft');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  /**
   * Asks the API for a signed link, then lets the browser fetch the file.
   *
   * Deliberately not a fetch into a blob: a two-hour recording is gigabytes,
   * and buffering one in the tab to attach an Authorization header would run
   * the page out of memory. The link carries its own short-lived credential
   * instead, so the browser streams it to disk the way it streams any download
   * - with a progress bar, and resumable.
   */
  async function handleDownload() {
    if (!liveId || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      const { url } = await recordingDownloadLink(liveId);
      startDownload(url);
    } catch (cause) {
      setDownloadError(errorMessage(cause));
    } finally {
      setDownloading(false);
    }
  }

  const [guestName, setGuestName] = useState('');
  const [addingGuest, setAddingGuest] = useState(false);
  const [guestError, setGuestError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const [pendingDelete, setPendingDelete] = useState<GuestInvite | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!live) {
      return;
    }
    setTitle(live.title);
    setDescription(live.description ?? '');
    setScheduledAt(toDateTimeLocalValue(live.scheduledAt));
    setStatus(live.status);
  }, [live]);

  if (loading) {
    return <LoadingScreen label="Loading live..." />;
  }

  if (!live) {
    return (
      <div className="empty-state">
        <h2>Live not available</h2>
        <p className="muted">{error ?? 'This live does not exist, or is not yours.'}</p>
        <Link className="button button--primary" to="/dashboard">
          Back to dashboard
        </Link>
      </div>
    );
  }

  const atCapacity = invites.length >= MAX_GUESTS;

  async function handleSave(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!live) {
      return;
    }
    setSaveError(null);
    setSaved(false);
    setSaving(true);
    try {
      const updated = await updateLive(live.id, {
        title: title.trim(),
        description: description.trim() || null,
        scheduledAt: fromDateTimeLocalValue(scheduledAt),
        status,
      });
      setLive(updated);
      setSaved(true);
    } catch (cause) {
      setSaveError(errorMessage(cause));
    } finally {
      setSaving(false);
    }
  }

  async function handleAddGuest(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!live) {
      return;
    }
    setGuestError(null);
    setAddingGuest(true);
    try {
      const invite = await createGuestInvite(live.id, { guestName: guestName.trim() });
      setInvites([...invites, invite]);
      setGuestName('');
    } catch (cause) {
      setGuestError(errorMessage(cause));
    } finally {
      setAddingGuest(false);
    }
  }

  async function handleCopy(invite: GuestInvite) {
    try {
      await copyToClipboard(inviteUrl(invite.token));
      setCopiedId(invite.id);
      window.setTimeout(() => setCopiedId(null), 2000);
    } catch (cause) {
      setGuestError(errorMessage(cause));
    }
  }

  async function handleDeleteInvite() {
    if (!pendingDelete) {
      return;
    }
    setDeleting(true);
    try {
      await deleteGuestInvite(pendingDelete.id);
      setInvites(invites.filter((invite) => invite.id !== pendingDelete.id));
      setPendingDelete(null);
    } catch (cause) {
      setGuestError(errorMessage(cause));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <nav className="breadcrumb">
        <Link to="/dashboard">Shows</Link>
        <span aria-hidden="true">/</span>
        <Link to={`/shows/${live.showId}`}>Show</Link>
        <span aria-hidden="true">/</span>
        <span>{live.title}</span>
      </nav>

      <div className="page-header">
        <div>
          <h1>{live.title}</h1>
          <p className="page-header__meta muted">
            <StatusBadge status={live.status} />{' '}
            <span className="mono">{live.livekitRoomName}</span>
          </p>
        </div>
        <Button variant="primary" onClick={() => navigate(`/lives/${live.id}/studio`)}>
          Enter studio
        </Button>
      </div>

      {/*
        The guests are what this page is for - links to send, people to let in -
        so they lead and take the wider column, with the settings beside them
        rather than in front of them. One column again below 1000px.
      */}
      <div className="page-grid">
        <section>
          <div className="section-title">
            <h2>Guests</h2>
            <span className="mono muted">
              {invites.length} of {MAX_GUESTS}
            </span>
          </div>

          <FormError>{guestError ?? invitesError}</FormError>

          <form className="row row--wrap list-form" onSubmit={handleAddGuest}>
            <input
              className="input"
              type="text"
              placeholder="Guest name"
              aria-label="Guest name"
              value={guestName}
              onChange={(event) => setGuestName(event.target.value)}
              disabled={atCapacity}
            />
            <Button
              type="submit"
              variant="primary"
              loading={addingGuest}
              disabled={atCapacity || !guestName.trim()}
            >
              Add guest
            </Button>
            {atCapacity ? (
              <span className="muted">
                A Live takes at most {MAX_GUESTS} guests. Remove one to add another.
              </span>
            ) : null}
          </form>

          {invitesLoading ? (
            <InlineLoader label="Loading guests..." />
          ) : invites.length === 0 ? (
            <div className="empty-state">
              <h3>No guests invited</h3>
              <p className="muted">
                Add a guest to generate an invite link. Guests need no account to join.
              </p>
            </div>
          ) : (
            <div className="list">
              {invites.map((invite) => (
                <div className="list__row" key={invite.id}>
                  <div className="list__main">
                    <span className="list__title">{invite.guestName}</span>
                    <span className="list__subtitle muted truncate invite-url mono">
                      {inviteUrl(invite.token)}
                    </span>
                    <span className="list__subtitle muted">
                      {invite.joinedAt
                        ? `First joined ${formatDateTime(invite.joinedAt)}`
                        : 'Not joined yet'}
                    </span>
                  </div>
                  <div className="list__actions row">
                    <Button variant="secondary" small onClick={() => handleCopy(invite)}>
                      {copiedId === invite.id ? 'Copied' : 'Copy link'}
                    </Button>
                    <Button variant="ghost" small onClick={() => setPendingDelete(invite)}>
                      Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="page-grid__aside">
          {/* Only once there is one. An empty "Recording" card on every live
              that was never recorded would be furniture. */}
          {live.recordingPath ? (
            <section className="card">
              <div className="card__header">
                <h2>Recording</h2>
              </div>
              <div className="stack">
                <p className="muted">
                  The program as it went out, composited server-side. The link is minted for you and
                  expires within the hour, so it cannot be forwarded to anyone.
                </p>
                <FormError>{downloadError}</FormError>
                <Button
                  variant="secondary"
                  disabled={downloading}
                  onClick={() => void handleDownload()}
                >
                  {downloading ? 'Preparing…' : 'Download the recording'}
                </Button>
              </div>
            </section>
          ) : null}

          <section className="card">
            <div className="card__header">
              <h2>Settings</h2>
            </div>

            <form className="stack" onSubmit={handleSave}>
              <FormError>{saveError}</FormError>
              {saved ? <p className="alert alert--info">Saved.</p> : null}

              <Field label="Title">
                {(id) => (
                  <input
                    id={id}
                    className="input"
                    type="text"
                    required
                    value={title}
                    onChange={(event) => setTitle(event.target.value)}
                  />
                )}
              </Field>

              <Field label="Description">
                {(id) => (
                  <textarea
                    id={id}
                    className="textarea"
                    rows={3}
                    value={description}
                    onChange={(event) => setDescription(event.target.value)}
                  />
                )}
              </Field>

              <div className="form-grid">
                <Field label="Scheduled for" hint="Your local time.">
                  {(id) => (
                    <input
                      id={id}
                      className="input"
                      type="datetime-local"
                      value={scheduledAt}
                      onChange={(event) => setScheduledAt(event.target.value)}
                    />
                  )}
                </Field>

                <Field label="Status">
                  {(id) => (
                    <select
                      id={id}
                      className="select"
                      value={status}
                      onChange={(event) => setStatus(event.target.value as LiveStatus)}
                    >
                      {LIVE_STATUSES.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>

              <div className="form-actions">
                <Button type="submit" variant="primary" loading={saving} disabled={!title.trim()}>
                  Save changes
                </Button>
              </div>
            </form>
          </section>
        </div>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Remove guest"
        message={
          pendingDelete
            ? `Remove "${pendingDelete.guestName}"? Their invite link stops working immediately.`
            : ''
        }
        confirmLabel="Remove"
        busy={deleting}
        onConfirm={handleDeleteInvite}
        onCancel={() => setPendingDelete(null)}
      />
    </>
  );
}
